import * as React from "react";
import {
  Cloud,
  Download,
  Folder,
  FolderOpen,
  HardDrive,
} from "lucide-react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { cn } from "../../lib/cn";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";

/**
 * DestinationPicker — interactive save-destination + folder selector for
 * the ``document_create`` HITL card.
 *
 * Architecture: pure presentational. Both the destination kind and the
 * folder list are fully controlled by the consumer — there's no data
 * fetching, no store binding, no router awareness here. Mirrors the
 * pattern of every other ``@ando/ui`` primitive (Drawer, FolderPicker,
 * VendorPreviewShell), so consumers can compose it in Storybook /
 * snapshot tests without a TanStack provider.
 *
 * UX contract (May 2026 SOTA — Linear/Notion/Vercel inspector cards):
 *
 *   1. **Eyebrow label** — "Salva in" (10px uppercase ink-3 tracking).
 *   2. **Destination radio row** — one chip per available destination,
 *      icons + label, ando-orange ring on the selected one. Disabled
 *      destinations (provider not connected) are filtered upstream via
 *      the ``availableDestinations`` BE-emitted list, so we never show
 *      a chip the user can't actually pick.
 *   3. **Folder section** — only when a cloud destination is picked.
 *      RadioGroup with "Cartella radice del Drive" as the first option
 *      (folder_id stays undefined → BE auto-creates "Ando Generated"
 *      under Drive root), then one row per tracked folder filtered by
 *      the picked provider. Skeleton on load, empty-state with deep-link
 *      to /workspace/integrations when no folders are tracked.
 *
 * Why a separate component (not extending the composer ``FolderPicker``):
 * the composer picker is multi-select + popover-driven + accepts ANY
 * tracked-folder id from any provider. This picker is single-select +
 * inline + provider-filtered + carries the destination kind as a sibling
 * dimension. Forcing one component to do both would explode the prop
 * surface (mode="single|multi", trigger="inline|popover", scope=…) and
 * break the YAGNI principle the rest of the package follows.
 */

export type SaveDestination =
  | "google_drive"
  | "onedrive"
  | "sharepoint"
  | "download"
  | "minio";

/** Subset of destinations that need a folder picker. */
export type CloudDestination = "google_drive" | "onedrive" | "sharepoint";

const CLOUD_DESTINATIONS: ReadonlySet<SaveDestination> = new Set<SaveDestination>([
  "google_drive",
  "onedrive",
  "sharepoint",
]);

/** Narrowing helper exported so consumers can branch type-safely. */
export function isCloudDestination(
  d: SaveDestination,
): d is CloudDestination {
  return CLOUD_DESTINATIONS.has(d);
}

export interface DestinationFolderItem {
  /** Provider-native folder id (Google Drive id, OneDrive driveItem id). */
  readonly folderId: string;
  /** Human-readable name. */
  readonly name: string;
  /** Optional virtual path ("/Vendite/Q2"). Shown muted when distinct. */
  readonly path?: string;
  readonly provider: CloudDestination;
}

export interface DestinationChoice {
  readonly destination: SaveDestination;
  /** Provider folder id. ``null`` = "root of provider". */
  readonly folderId: string | null;
  /** Human-readable folder label for proposal-card display. */
  readonly folderName: string | null;
}

export interface DestinationPickerProps {
  /** Destinations the BE reported as connected + writable. */
  readonly availableDestinations: ReadonlyArray<SaveDestination>;
  /** Current selection (fully controlled). */
  readonly value: DestinationChoice;
  /** Selection changed. Caller persists. */
  readonly onChange: (next: DestinationChoice) => void;
  /** Folders the user has tracked (filtered upstream OR here by provider). */
  readonly folders: ReadonlyArray<DestinationFolderItem>;
  /** True while folders are being fetched. */
  readonly isLoadingFolders?: boolean;
  /** Open the integrations page (no-folders empty state CTA). */
  readonly onOpenIntegrations?: () => void;
  readonly className?: string;
  /** Disable the picker (e.g. while the action is executing). */
  readonly disabled?: boolean;
}

const DESTINATION_LABEL: Record<SaveDestination, MessageDescriptor> = {
  google_drive: msg`Google Drive`,
  onedrive: msg`OneDrive`,
  sharepoint: msg`SharePoint`,
  download: msg`Download diretto`,
  minio: msg`Archivio Ando`,
};

const DESTINATION_ICON: Record<
  SaveDestination,
  React.ComponentType<{ className?: string }>
> = {
  google_drive: Cloud,
  onedrive: Cloud,
  sharepoint: Cloud,
  download: Download,
  minio: HardDrive,
};

// ``__root__`` is the sentinel ``RadioGroup.value`` for "no folder
// picked → save to provider root". Radix RadioGroup requires a
// non-empty string value, so we route ``null`` ↔ this constant at the
// component boundary. Module-level so the producer (FolderRow value)
// and the consumer (handleFolderChange) can never drift.
const ROOT_FOLDER_SENTINEL = "__root__";

export const DestinationPicker: React.FC<DestinationPickerProps> = ({
  availableDestinations,
  value,
  onChange,
  folders,
  isLoadingFolders = false,
  onOpenIntegrations,
  className,
  disabled = false,
}) => {
  const { i18n } = useLingui();
  // Defensive: BE may emit destinations the FE doesn't render (future
  // provider). Filter to the known set so the radio doesn't blow up.
  const renderable = React.useMemo(
    () =>
      availableDestinations.filter(
        (d): d is SaveDestination => d in DESTINATION_LABEL,
      ),
    [availableDestinations],
  );

  const foldersForProvider = React.useMemo(() => {
    if (!isCloudDestination(value.destination)) return [];
    return folders.filter((f) => f.provider === value.destination);
  }, [folders, value.destination]);

  const handleDestinationChange = React.useCallback(
    (nextDest: string) => {
      // Picking a NEW destination clears the folder — old folder ids
      // belong to the previous provider's namespace and would 404 at
      // upload time. Caller still owns persistence.
      onChange({
        destination: nextDest as SaveDestination,
        folderId: null,
        folderName: null,
      });
    },
    [onChange],
  );

  const handleFolderChange = React.useCallback(
    (nextFolderId: string) => {
      if (nextFolderId === ROOT_FOLDER_SENTINEL) {
        onChange({ ...value, folderId: null, folderName: null });
        return;
      }
      const picked = foldersForProvider.find(
        (f) => f.folderId === nextFolderId,
      );
      onChange({
        ...value,
        folderId: nextFolderId,
        folderName: picked?.name ?? null,
      });
    },
    [foldersForProvider, onChange, value],
  );

  if (renderable.length === 0) {
    // No destination available at all — show the read-only fallback so
    // the user sees "where will it go" even when picking is impossible.
    return (
      <div className={cn("text-[12px] text-ink-3", className)}>
        {i18n._(
          msg`Nessuna destinazione configurata. Il file resterà nell'archivio interno di Ando.`,
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <DestinationRow
        destinations={renderable}
        value={value.destination}
        onChange={handleDestinationChange}
        disabled={disabled}
      />

      {isCloudDestination(value.destination) && (
        <FolderSection
          provider={value.destination}
          folderId={value.folderId}
          folders={foldersForProvider}
          isLoading={isLoadingFolders}
          onChange={handleFolderChange}
          onOpenIntegrations={onOpenIntegrations}
          disabled={disabled}
        />
      )}
    </div>
  );
};
DestinationPicker.displayName = "DestinationPicker";

// ─── Subcomponents ──────────────────────────────────────────────────────────

interface DestinationRowProps {
  readonly destinations: ReadonlyArray<SaveDestination>;
  readonly value: SaveDestination;
  readonly onChange: (next: string) => void;
  readonly disabled: boolean;
}

const DestinationRow: React.FC<DestinationRowProps> = ({
  destinations,
  value,
  onChange,
  disabled,
}) => {
  const { i18n } = useLingui();
  return (
  <div>
    <div className="mb-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
      {i18n._(msg`Salva in`)}
    </div>
    <RadioGroup
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      // Override the default vertical stack — destination chips read
      // better as a horizontal row (icons + label, ≤5 options).
      className="flex flex-row flex-wrap gap-1.5"
      aria-label={i18n._(msg`Destinazione di salvataggio`)}
    >
      {destinations.map((d) => {
        const Icon = DESTINATION_ICON[d];
        const checked = value === d;
        // Each chip pairs a hidden RadioGroupItem (keyboard + SR target)
        // with a label that carries the visible chip styling. Explicit
        // ``htmlFor`` → ``id`` association keeps a11y linters happy
        // AND lets the user click anywhere on the chip to select.
        const itemId = `dc-dest-${d}`;
        return (
          <label
            key={d}
            htmlFor={itemId}
            className={cn(
              "inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium text-[11.5px] transition-colors",
              checked
                ? "border-ando-orange/55 bg-ando-orange/10 text-ando-orange"
                : "border-hairline bg-white/60 text-ink-2 hover:bg-ink-1/[0.04]",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <RadioGroupItem
              id={itemId}
              value={d}
              // The visible chip carries the orange ring; hide the
              // radio circle inside (still keyboard reachable, still
              // SR-readable through aria-label on the radio item).
              className="sr-only"
              aria-label={i18n._(DESTINATION_LABEL[d])}
            />
            <Icon className="h-3 w-3" aria-hidden />
            <span>{i18n._(DESTINATION_LABEL[d])}</span>
          </label>
        );
      })}
    </RadioGroup>
  </div>
  );
};

interface FolderSectionProps {
  readonly provider: CloudDestination;
  readonly folderId: string | null;
  readonly folders: ReadonlyArray<DestinationFolderItem>;
  readonly isLoading: boolean;
  readonly onChange: (folderId: string) => void;
  readonly onOpenIntegrations?: () => void;
  readonly disabled: boolean;
}

const FolderSection: React.FC<FolderSectionProps> = ({
  provider,
  folderId,
  folders,
  isLoading,
  onChange,
  onOpenIntegrations,
  disabled,
}) => {
  const { i18n } = useLingui();
  const radioValue = folderId ?? ROOT_FOLDER_SENTINEL;
  // Reuse the single source of truth — ``DESTINATION_LABEL`` already
  // maps every provider to its human name. Inline branching here was
  // a duplicate that would drift the day the brand renames a provider.
  const providerLabel = i18n._(DESTINATION_LABEL[provider]);

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <div className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
          {i18n._(msg`Cartella ${providerLabel.toLowerCase()}`)}
        </div>
        {folders.length > 0 && (
          <span className="text-[10px] text-ink-3">
            {i18n._(msg`${folders.length} disponibili`)}
          </span>
        )}
      </div>

      {isLoading ? (
        <FolderSkeleton />
      ) : (
        <RadioGroup
          value={radioValue}
          onValueChange={onChange}
          disabled={disabled}
          className="max-h-44 overflow-y-auto rounded-md border border-hairline bg-white/40"
          aria-label={i18n._(msg`Cartella di destinazione (${providerLabel})`)}
        >
          <FolderRow
            value={ROOT_FOLDER_SENTINEL}
            label={i18n._(msg`Cartella radice`)}
            hint={
              provider === "google_drive"
                ? i18n._(msg`Ando crea automaticamente una cartella “Ando Generated”.`)
                : i18n._(msg`Salva alla radice di ${providerLabel}.`)
            }
            checked={folderId === null}
          />
          {folders.map((f) => (
            <FolderRow
              key={f.folderId}
              value={f.folderId}
              label={f.name}
              hint={f.path && f.path !== f.name ? f.path : undefined}
              checked={folderId === f.folderId}
            />
          ))}
          {folders.length === 0 && (
            <FolderEmpty
              providerLabel={providerLabel}
              onOpenIntegrations={onOpenIntegrations}
            />
          )}
        </RadioGroup>
      )}
    </div>
  );
};

interface FolderRowProps {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
  readonly checked: boolean;
}

const FolderRow: React.FC<FolderRowProps> = ({
  value,
  label,
  hint,
  checked,
}) => {
  // Stable id per row so click-anywhere works and a11y linters resolve
  // the label→control association without nesting a real <input>.
  const itemId = `dc-folder-${value}`;
  return (
    <label
      htmlFor={itemId}
      className={cn(
        "flex cursor-pointer items-center gap-2 border-hairline/60 border-b px-2.5 py-2 transition-colors last:border-b-0",
        "hover:bg-ink-1/[0.04]",
        checked && "bg-ando-orange/[0.06]",
      )}
    >
      <RadioGroupItem
        id={itemId}
        value={value}
        aria-label={hint ? `${label} (${hint})` : label}
      />
      {checked ? (
        <FolderOpen aria-hidden className="h-3.5 w-3.5 shrink-0 text-ando-orange" />
      ) : (
        <Folder aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-3" />
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[12px] text-ink-1">{label}</span>
        {hint && (
          <span className="truncate text-[10.5px] text-ink-3">{hint}</span>
        )}
      </span>
    </label>
  );
};

const FOLDER_SKELETON_KEYS = ["s1", "s2", "s3"] as const;

function FolderSkeleton(): React.ReactElement {
  return (
    <div
      aria-hidden
      className="space-y-2 rounded-md border border-hairline bg-white/40 px-2.5 py-2"
    >
      {FOLDER_SKELETON_KEYS.map((k) => (
        <div key={k} className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full border border-hairline bg-ink-1/[0.04]" />
          <span className="h-3 w-3 rounded bg-ink-1/[0.04]" />
          <span className="h-3 flex-1 rounded bg-ink-1/[0.04]" />
        </div>
      ))}
    </div>
  );
}

interface FolderEmptyProps {
  readonly providerLabel: string;
  readonly onOpenIntegrations?: () => void;
}

const FolderEmpty: React.FC<FolderEmptyProps> = ({
  providerLabel,
  onOpenIntegrations,
}) => {
  const { i18n } = useLingui();
  return (
  <div className="px-2.5 py-3 text-center">
    <p className="text-[11.5px] text-ink-2">
      {i18n._(msg`Nessuna cartella ${providerLabel} tracciata.`)}
    </p>
    <p className="mt-0.5 text-[10.5px] text-ink-3">
      {i18n._(msg`Il file resta nella cartella radice se confermi.`)}
    </p>
    {onOpenIntegrations && (
      <button
        type="button"
        onClick={onOpenIntegrations}
        className="mt-2 rounded-md border border-hairline bg-white px-2.5 py-1 font-medium text-[11px] text-ink-1 transition-colors hover:bg-ink-1/[0.04]"
      >
        {i18n._(msg`Apri integrazioni`)}
      </button>
    )}
  </div>
  );
};
